import React from 'react';
import Head from 'next/head';

import { useTranslation } from 'providers/translations/TranslationProvider';
import assemblePath from 'utils/assemblePath';
import { WEBSITE_CDN } from 'utils/config';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import { AlternateLink } from 'data/market/types';

import AlternateLinkTags from './AlternateLinkTags/AlternateLinkTags';
import OgTags from './OgTags/OgTags';
import AppleTags from './AppleTags/AppleTags';
import TwitterTags from './TwitterTags/TwitterTags';

interface MetatagsProps {
  urlPath?: string;
  title?: string;
  desc?: string;
  keywords?: string;
  noIndex?: boolean;
  alternateLinks?: AlternateLink[];
}

const useCanonicalUrl = (urlPath: string): string => {
  const {
    verticalConfig: { pathParam },
    market: { marketCode },
  } = useServerSideConstants();

  return `https://support.spotify.com${assemblePath(marketCode, pathParam, urlPath)}`;
};

const Metatags = ({
  urlPath = '',
  title: customTitle,
  desc,
  keywords,
  noIndex = false,
  alternateLinks = [],
}: MetatagsProps) => {
  const { t } = useTranslation();

  const url = useCanonicalUrl(urlPath);
  const title = customTitle || t('title_tag_default');

  return (
    <>
      <Head>
        <title>{`${title} - Spotify`}</title>
        <link rel="icon" href={`${WEBSITE_CDN}/favicon.png`} />
        <link rel="canonical" href={url} />
        {desc && <meta name="description" content={desc} />}
        {keywords && <meta name="keywords" content={keywords} />}
        {noIndex && <meta name="robots" content="noindex" />}
        {alternateLinks.length > 0 && <AlternateLinkTags alternateLinks={alternateLinks} />}
      </Head>

      <OgTags url={url} title={title} desc={desc} />
      <TwitterTags url={url} title={title} desc={desc} />
      <AppleTags />
    </>
  );
};

export default Metatags;
